import imageCompression from 'browser-image-compression';

/**
 * Función utilitaria para obtener la URL correcta de una imagen
 * Maneja tanto URLs completas (S3) como rutas relativas (almacenamiento local)
 */
export const getImageUrl = (photo: string, backendUrl: string): string => {
  // Si la foto ya es una URL completa (empieza con http/https), usar directamente
  if (photo.startsWith('http://') || photo.startsWith('https://')) {
    return photo;
  }
  // Si es una ruta relativa que empieza con /uploads/, construir la URL completa
  if (photo.startsWith('/uploads/')) {
    const localUrl = `${backendUrl}${photo}`;
    return localUrl;
  }
  // Si es solo el nombre del archivo, agregar la ruta /uploads/
  const localUrl = `${backendUrl}/uploads/${photo}`;
  return localUrl;
};

/**
 * Función para obtener URLs para el PDF - usa el proxy del backend para evitar CORS
 */
export const getImageUrlForPDF = (photo: string, backendUrl: string): string => {
  // Si la foto ya es una URL completa (empieza con http/https), verificar si es S3
  if (photo.startsWith('http://') || photo.startsWith('https://')) {
    // Si es una URL de S3, construir la URL del proxy del backend
    if (photo.includes('s3.amazonaws.com') || photo.includes('s3.sa-east-1.amazonaws.com')) {
      try {
        // Extraer de forma segura la ruta del archivo (ej: uploads/imagen.jpg)
        const s3Url = new URL(photo);
        const key = s3Url.pathname.substring(1); // Eliminar el primer "/"
        const proxyUrl = `${backendUrl}/tasks/image/${key}`;
        console.log(`[getImageUrlForPDF] URL de S3 detectada. Usando proxy: ${proxyUrl}`);
        return proxyUrl;
      } catch (error) {
        console.error('URL de S3 inválida, no se puede crear proxy:', photo, error);
        return photo; // Fallback a la URL directa si hay un error
      }
    }
    // Si no es S3, usar la URL directamente
    return photo;
  }

  // Para rutas locales relativas (ej: /uploads/imagen.jpg), construir la URL completa
  if (photo.startsWith('/uploads/')) {
    return `${backendUrl}${photo}`;
  }
  
  // Fallback para nombres de archivo sin ruta
  return `${backendUrl}/uploads/${photo}`;
};

/**
 * Función para obtener URLs directas de S3 para el PDF
 */
export const getImageUrlForPDFDirect = (photo: string): string => {
  // Si la foto ya es una URL completa, usar directamente
  if (photo.startsWith('http://') || photo.startsWith('https://')) {
    return photo;
  }
  // Para URLs locales, usar la URL completa
  return photo;
};

/**
 * Compresses an image file to reduce its size before upload.
 * @param file The file to compress
 * @returns The compressed file
 */
export const compressImage = async (file: File): Promise<File> => {
  // Opciones de compresión
  const options = {
    maxSizeMB: 1,           // Tamaño máximo deseado en MB
    maxWidthOrHeight: 1920, // Resolución máxima (Full HD)
    useWebWorker: true      // Usar web worker para mejor rendimiento
  };

  try {
    // Si el archivo no es una imagen, devolverlo tal cual (ej. PDF)
    if (!file.type.startsWith('image/')) {
      return file;
    }

    const compressedFile = await imageCompression(file, options);
    
    // Si por alguna razón la compresión falla o retorna algo inválido, devolver original
    return compressedFile || file;
  } catch (error) {
    console.error('Error al comprimir la imagen:', error);
    // En caso de error, devolver el archivo original para no bloquear la subida
    return file;
  }
};

/**
 * Compresses multiple image files
 * @param files Array of files to compress
 * @returns Array of compressed files
 */
export const compressImages = async (files: File[]): Promise<File[]> => {
  const promises = files.map(file => compressImage(file));
  return Promise.all(promises);
}; 